import { CenteredDialog } from '../../components/ui/centered-dialog';
import { LookupCombobox } from '../../components/ui/lookup-combobox';
import { DatePicker } from '../../components/ui/date-picker';
// apps/web/src/pages/relatorios/g7-page.tsx — Fase 02, Plano 11 (REM-02, EST-04, D-15, D-16, Gate G7).
import { useState, type ReactElement } from 'react';
import type {
  RelatorioG7Resultado,
  LoteConciliadoG7,
} from '@pieltec/contracts';
import { apiMutate } from '../../lib/csrf-fetch';

export function G7Page(): ReactElement {
  // Filtros obrigatórios (UI: sem tabela vazia por padrão)
  const [clienteId, setClienteId] = useState<string>('');
  const [opId, setOpId] = useState<string>('');
  const [loteId, setLoteId] = useState<string>('');
  const [dataInicio, setDataInicio] = useState<string>('');
  const [dataFim, setDataFim] = useState<string>('');

  const [relatorio, setRelatorio] = useState<RelatorioG7Resultado | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Modal de Aprovação G7
  const [loteParaAprovar, setLoteParaAprovar] = useState<LoteConciliadoG7 | null>(null);
  const [justificativaAprovacao, setJustificativaAprovacao] = useState<string>('');
  const [aprovando, setAprovando] = useState<boolean>(false);
  const [resultadoAprovacao, setResultadoAprovacao] = useState<{ aprovado: boolean; motivos?: string[] } | null>(null);

  const hasActiveFilter = Boolean(clienteId.trim() || opId.trim() || loteId.trim() || dataInicio.trim());

  const handleBuscar = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!hasActiveFilter) return;

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (clienteId.trim()) params.set('clienteId', clienteId.trim());
      if (opId.trim()) params.set('opId', opId.trim());
      if (loteId.trim()) params.set('loteId', loteId.trim());
      if (dataInicio.trim()) params.set('dataInicio', dataInicio.trim());
      if (dataFim.trim()) params.set('dataFim', dataFim.trim());

      const res = await fetch(`/api/v1/relatorios/g7?${params.toString()}`);
      if (!res.ok) {
        const errJson = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(errJson.message || 'Falha ao consultar relatório conciliado G7.');
      }
      const data = (await res.json()) as RelatorioG7Resultado;
      setRelatorio(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao consultar relatório');
    } finally {
      setLoading(false);
    }
  };

  const handleAprovarGate = async () => {
    if (!loteParaAprovar || !justificativaAprovacao.trim()) return;

    setAprovando(true);
    setResultadoAprovacao(null);
    try {
      const res = await apiMutate('/api/v1/relatorios/g7/aprovar', {
        method: 'POST',
        body: {
          loteId: loteParaAprovar.loteId,
          justificativa: justificativaAprovacao.trim(),
        },
      });

      const data = (await res.json()) as { aprovado: boolean; motivosBloqueio?: string[] };
      if (!res.ok || !data.aprovado) {
        setResultadoAprovacao({ aprovado: false, motivos: data.motivosBloqueio ?? ['Aprovação bloqueada'] });
      } else {
        setResultadoAprovacao({ aprovado: true });
        void handleBuscar();
      }
    } catch (err) {
      setResultadoAprovacao({
        aprovado: false,
        motivos: [err instanceof Error ? err.message : 'Erro ao aprovar Gate G7'],
      });
    } finally {
      setAprovando(false);
    }
  };

  return (
    <div className="g7-page min-h-screen bg-slate-100 p-4 md:p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header Principal */}
        <header className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 bg-blue-100 text-blue-800 text-[11px] font-bold uppercase rounded-full">
                Rastreabilidade e conciliação
              </span>
              <h1 className="text-2xl font-bold text-slate-900">Relatório Conciliado Ponta a Ponta</h1>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Rastreabilidade do material da remessa ao retorno, reconciliação de saldo e fechamento formal de gate
            </p>
          </div>
        </header>

        {/* Filtro Obrigatório */}
        <form
          onSubmit={(e) => void handleBuscar(e)}
          className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4"
        >
          <h2 className="text-xs font-bold text-slate-700 uppercase tracking-wider">Filtro Obrigatório</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div>
              <label htmlFor="g7-clienteId" className="text-sm font-medium">Cliente</label><LookupCombobox id="g7-clienteId" endpoint="/api/v1/relatorios/lookups/clienteId" label="Cliente" valueKey="codigo" value={clienteId} onChange={setClienteId} />
            </div>
            <div>
              <label htmlFor="g7-opId" className="text-sm font-medium">Ordem de produção</label><LookupCombobox id="g7-opId" endpoint="/api/v1/relatorios/lookups/opId" label="Ordem de produção" valueKey="id" value={opId} onChange={setOpId} />
            </div>
            <div>
              <label htmlFor="g7-loteId" className="text-sm font-medium">Lote</label><LookupCombobox id="g7-loteId" endpoint="/api/v1/relatorios/lookups/loteId" label="Lote" valueKey="id" value={loteId} onChange={setLoteId} />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1" htmlFor="field-g7-page-6215">A partir de (Data):</label>
              <DatePicker id="field-g7-page-6215"
                value={dataInicio}
                onValueChange={(value) => setDataInicio(value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg font-mono focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div><label htmlFor="g7-data-fim" className="block text-sm mb-1">Até a data</label><DatePicker id="g7-data-fim" value={dataFim} onValueChange={setDataFim} /></div>
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={!hasActiveFilter || loading}
              className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-xs font-bold rounded-xl shadow-sm transition"
            >
              {loading ? 'Processando Conciliação...' : 'Gerar Relatório Conciliado'}
            </button>
          </div>
        </form>

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl font-medium" role="alert">
            {error}
          </div>
        )}

        {/* Estado sem filtro aplicado */}
        {!relatorio && !loading && (
          <div className="py-16 text-center bg-white rounded-2xl border border-slate-200 p-8 space-y-2">
            <div className="text-sm font-bold text-slate-700">Selecione cliente, período ou OP para consultar.</div>
            <p className="text-xs text-slate-400 max-w-md mx-auto">
              Para garantir performance sobre grandes volumes de materiais, aplique ao menos um critério de busca acima.
            </p>
          </div>
        )}

        {/* Resultado do Relatório Conciliado */}
        {relatorio && !loading && (
          <main className="space-y-6">
            {/* Banner de Reconciliação Geral */}
            <div className="bg-slate-900 text-white p-6 rounded-2xl shadow-xl flex flex-col md:flex-row md:items-center justify-between gap-4 font-mono text-xs">
              <div>
                <span className="text-[10px] text-blue-400 font-bold uppercase tracking-wider block">
                  Reconciliação Matemática de Saldo
                </span>
                <span className="text-lg font-bold">
                  {relatorio.lotes.length} lote(s) conciliado(s) no recorte
                </span>
              </div>

              <div className="flex items-center gap-6">
                <div>
                  <span className="text-slate-400 block text-[10px]">TOTAL ENTRADA:</span>
                  <span className="text-sm font-bold text-emerald-400">{relatorio.totalEntrada} un</span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px]">TOTAL RETORNADO:</span>
                  <span className="text-sm font-bold text-blue-400">{relatorio.totalRetorno} un</span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[10px]">DIVERGÊNCIA:</span>
                  <span
                    className={`text-sm font-bold ${
                      relatorio.divergenciaGeral === 0 ? 'text-emerald-400' : 'text-red-400'
                    }`}
                  >
                    {relatorio.divergenciaGeral === 0 ? '✓ ZERO (Reconciliado)' : `${relatorio.divergenciaGeral} un`}
                  </span>
                </div>
              </div>
            </div>

            {/* Listagem de Lotes e Timelines Expansíveis */}
            {relatorio.lotes.length === 0 ? (
              <div className="py-12 text-center text-xs text-slate-400 bg-white rounded-2xl border border-slate-200">
                Nenhum registro para o filtro selecionado.
              </div>
            ) : (
              relatorio.lotes.map((lote) => (
                <LoteCardG7
                  key={lote.loteId}
                  lote={lote}
                  onAprovarClick={() => {
                    setLoteParaAprovar(lote);
                    setJustificativaAprovacao('');
                    setResultadoAprovacao(null);
                  }}
                />
              ))
            )}
          </main>
        )}
      </div>

      {/* Modal de Aprovação de Gate G7 */}
      {loteParaAprovar && (
        <CenteredDialog title="Aprovação Formal do Gate G7 — Lote" onClose={() => setLoteParaAprovar(null)}>
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-lg w-full p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="text-base font-bold text-slate-900">
                Aprovação Formal do Gate G7 — Lote {loteParaAprovar.loteCodigo}
              </h3>
              <button
                type="button"
                onClick={() => setLoteParaAprovar(null)}
                className="text-slate-400 hover:text-slate-600 font-bold text-base"
              >
                ✕
              </button>
            </div>

            <p className="text-xs text-slate-600">
              Aprovar fecha o gate G7 para o lote selecionado após verificação formal dos gates de qualidade e
              conciliação de saldo.
            </p>

            {resultadoAprovacao && !resultadoAprovacao.aprovado && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl space-y-1" role="alert">
                <span className="font-bold block">Aprovação Bloqueada pelos Gates D-16:</span>
                {resultadoAprovacao.motivos?.map((m) => (
                  <div key={m} className="text-[11px]">
                    • {m}
                  </div>
                ))}
              </div>
            )}

            {resultadoAprovacao && resultadoAprovacao.aprovado && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs rounded-xl font-bold">
                ✓ Gate G7 aprovado e assinado com sucesso!
              </div>
            )}

            <div className="space-y-1">
              <label className="block text-[11px] font-bold text-slate-700 uppercase" htmlFor="field-g7-page-12246">
                Justificativa Formal (Obrigatória) *
              </label>
              <textarea id="field-g7-page-12246"
                required
                rows={3}
                value={justificativaAprovacao}
                onChange={(e) => setJustificativaAprovacao(e.target.value)}
                placeholder="Insira a justificativa formal da aprovação conjunta PCP + Qualidade..."
                className="w-full px-3 py-2 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex justify-end gap-3 pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setLoteParaAprovar(null)}
                className="px-4 py-2 border border-slate-300 text-slate-700 text-xs font-semibold rounded-lg hover:bg-slate-50"
              >
                Fechar
              </button>
              <button
                type="button"
                disabled={aprovando || !justificativaAprovacao.trim()}
                onClick={() => void handleAprovarGate()}
                className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg shadow transition disabled:opacity-40"
              >
                {aprovando ? 'Avaliando Gates...' : 'Assinar e Aprovar Gate G7'}
              </button>
            </div>
          </div>
        </CenteredDialog>
      )}
    </div>
  );
}

function LoteCardG7({
  lote,
  onAprovarClick,
}: {
  readonly lote: LoteConciliadoG7;
  readonly onAprovarClick: () => void;
}): ReactElement {
  const [expanded, setExpanded] = useState<boolean>(false);

  return (
    <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      {/* Cabeçalho do Card */}
      <div className="p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 bg-slate-900 text-white font-mono text-xs font-bold rounded">
              {lote.loteCodigo}
            </span>
            <span className="text-sm font-bold text-slate-800">{lote.itemId}</span>
            {lote.op && (
              <span className="text-xs text-slate-500 font-mono">
                · OP-{lote.op.numero} ({lote.op.status})
              </span>
            )}
          </div>
          <div className="text-xs text-slate-400 font-mono">
            Remessa: {lote.remessaId?.slice(0, 8) ?? 'N/A'} · Cliente: {lote.clienteId ?? 'N/A'}
          </div>
        </div>

        {/* Saldos Conciliados */}
        <div className="flex items-center gap-4 text-xs font-mono">
          <div>
            <span className="text-slate-400 block text-[10px]">Entrada:</span>
            <span className="font-bold text-slate-700">{lote.saldoEntrada} un</span>
          </div>
          <div>
            <span className="text-slate-400 block text-[10px]">Consumo:</span>
            <span className="font-bold text-slate-700">{lote.saldoConsumo} un</span>
          </div>
          <div>
            <span className="text-slate-400 block text-[10px]">Retornado:</span>
            <span className="font-bold text-blue-600">{lote.saldoRetorno} un</span>
          </div>
          <div>
            <span className="text-slate-400 block text-[10px]">Saldo Ledger:</span>
            <span className="font-bold text-emerald-600">{lote.saldoLedger} un</span>
          </div>

          <div className="flex items-center gap-2 ml-2">
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="px-3 py-1.5 border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-bold rounded-lg transition"
            >
              {expanded ? 'Ocultar Trilha' : `Ver Trilha (${lote.timeline.length})`}
            </button>
            <button
              type="button"
              onClick={onAprovarClick}
              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg shadow-sm transition"
            >
              Aprovar G7
            </button>
          </div>
        </div>
      </div>

      {/* Timeline Expansível Read-Only */}
      {expanded && (
        <div className="p-6 bg-slate-50 border-t border-slate-100 space-y-4">
          <h4 className="text-xs font-bold text-slate-600 uppercase tracking-wider">
            Trilha Imutável de Eventos do Lote
          </h4>
          <div className="relative pl-6 border-l-2 border-slate-200 space-y-4 text-xs font-mono">
            {lote.timeline.map((ev) => (
              <div key={ev.id} className="relative group">
                <span className="absolute -left-[31px] top-1.5 w-3 h-3 rounded-full bg-blue-500 border-2 border-white shadow-sm" />
                <div className="bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-slate-800 text-[11px] px-2 py-0.5 rounded bg-slate-100">
                      {ev.tipo}
                    </span>
                    <span className="text-slate-400 text-[10px]">{new Date(ev.data).toLocaleString()}</span>
                  </div>
                  {ev.justificativa && (
                    <div className="text-slate-600 text-[11px] pt-1">
                      <span className="font-semibold text-slate-500">Motivo/Justificativa:</span> {ev.justificativa}
                    </div>
                  )}
                  <div className="text-[10px] text-slate-400 pt-0.5">
                    Ator: {ev.ator ? ev.ator.slice(0, 8) : 'Sistema'} · Detalhes: {JSON.stringify(ev.detalhes)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
